<template>
  <div class="reward-page-wrap">
    <!-- Header -->
    <header class="reward-header">
      <div class="reward-header__back" @click="goBack">
        <van-icon name="arrow-left" size="20" color="#ffffff" />
      </div>
      <h1 class="reward-header__title">{{ $lang.reward_history || 'REWARD HISTORY' }}</h1>
      <div class="reward-header__filter" @click="showDatePicker = true">
        <van-icon name="notes-o" size="22" color="#ffffff" />
      </div>
    </header>

    <!-- 当前筛选日期提示 -->
    <div v-if="filterDate" class="reward-filter-tag">
      <span>{{ formatFilterDate(filterDate) }}</span>
      <van-icon name="cross" size="14" color="#ff5a1f" @click="clearFilter" />
    </div>

    <!-- Main Content -->
    <main class="reward-content">
      <div v-if="readError" class="reward-read-error">
        {{ readError }}
      </div>

      <template v-for="(group, gIdx) in groupedRewards">
        <section :key="gIdx" class="reward-group">
          <!-- Sticky Month Header -->
          <div class="reward-group__header">
            <h3>{{ group.month }}</h3>
          </div>
          <!-- Reward Items -->
          <div class="reward-group__list">
            <div
              v-for="(item, idx) in group.items"
              :key="idx"
              class="reward-item"
            >
              <!-- Icon -->
              <div class="reward-item__icon">
                <img src="@/assets/img/activity/activity_icon/checkin/coin.png" alt="coin" width="100%" height="100%" />
              </div>
              <!-- Info -->
              <div class="reward-item__info">
                <div class="reward-item__title-row">
                  <p class="reward-item__type">{{ item.type === 'deposit' ? ($lang.reward_deposit_signin || 'Deposit Sign-in') : ($lang.reward_regular_signin || 'Regular Sign-in') }}</p>
                  <span v-if="item.isPremium" class="reward-item__premium">{{ $lang.reward_premium || 'PREMIUM' }}</span>
                </div>
                <p class="reward-item__date">{{ formatDate(item.date) }}</p>
              </div>
              <!-- Amount -->
              <div class="reward-item__amount">
                <p>+{{ formatAmount(item.amount) }}</p>
              </div>
            </div>
          </div>
        </section>
      </template>

      <!-- Empty State -->
      <div v-if="groupedRewards.length === 0" class="reward-empty">
        <van-icon name="search" size="48" color="var(--vk-t3)" />
        <p>{{ $lang.reward_no_data || 'No rewards found' }}</p>
      </div>

      <button
        v-if="groupedRewards.length > 0 && !finished"
        class="reward-load-more"
        :disabled="loading"
        @click="loadMore"
      >
        {{ loading ? ($lang.common_loading || 'Loading...') : ($lang.rp_load_more || 'Load More') }}
      </button>
    </main>

    <!-- Date Picker Popup -->
    <DatePickerPopup
      v-model="showDatePicker"
      :min-date="minDate"
      :max-date="maxDate"
      :default-date="maxDate"
      :confirm-text="$lang.reward_confirm || 'Confirm Selection'"
      :cancel-text="$lang.reward_cancel || 'Cancel'"
      @confirm="onDateConfirm"
    />
  </div>
</template>

<script>
import { GetSignDaily } from '@/api/common'
import DatePickerPopup from '@/components/DatePickerPopup'
import { failMessage as rewardFailMessage, isNeedLogin, isOkResponse, responseContent } from './rewardApi'
import { hasServerProvenEmptyRead, hasUnavailableMarker } from './rewardApi'

const REWARD_HISTORY_LIST_KEYS = [
  'rewardHistoryList',
  'signRecordList',
  'signHistoryList',
  'list',
  'records',
  'dataList',
  'pageList',
  'items'
]
const REWARD_HISTORY_ENVELOPE_KEYS = ['content', 'data', 'payload', 'page', 'body', 'records']

export default {
  name: 'RewardHistory',
  components: { DatePickerPopup },
  data() {
    const now = new Date()
    return {
      showDatePicker: false,
      filterDate: null,
      minDate: new Date(now.getFullYear(), now.getMonth(), 1),
      maxDate: now,
      rewardList: [],
      pageIndex: 0,
      pageSize: 20,
      finished: false,
      loading: false,
      readError: ''
    }
  },
  computed: {
    filteredRewards() {
      if (!this.filterDate) return this.rewardList
      const y = this.filterDate.getFullYear()
      const m = this.filterDate.getMonth()
      const d = this.filterDate.getDate()
      return this.rewardList.filter(item => {
        const dt = new Date(item.date)
        return dt.getFullYear() === y && dt.getMonth() === m && dt.getDate() === d
      })
    },
    groupedRewards() {
      const groups = {}
      this.filteredRewards.forEach(item => {
        const dt = new Date(item.date)
        if (isNaN(dt.getTime())) return
        const monthKey = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
        const label = `${monthNames[dt.getMonth()]} ${dt.getFullYear()}`
        if (!groups[monthKey]) {
          groups[monthKey] = { month: label, items: [] }
        }
        groups[monthKey].items.push(item)
      })
      return Object.keys(groups)
        .sort((a, b) => b.localeCompare(a))
        .map(k => groups[k])
    }
  },
  async mounted() {
    await this.fetchRewards()
  },
  methods: {
    goBack() {
      this.$router.go(-1)
    },
    failMessage(res, fallback) {
      return rewardFailMessage(res, fallback || '活动请求失败，请稍后重试。')
    },
    preserveReadError(res, fallback) {
      const message = this.failMessage(res, fallback)
      this.readError = `读取失败，已保留已加载记录。${message}`
      this.$toast({
        message: this.readError,
        icon: 'cross'
      })
    },
    isServerProvenEmptyRead(res) {
      return typeof hasServerProvenEmptyRead === 'function' && hasServerProvenEmptyRead(res)
    },
    formatAmount(amount) {
      return this.$formatNumberWithCommas(amount) || '0'
    },
    async fetchRewards() {
      if (this.loading) return
      this.loading = true
      try {
        const res = await GetSignDaily({ pageIndex: this.pageIndex, pageSize: this.pageSize })
        if (isNeedLogin(res)) {
          this.$bus.$emit('openLogin')
        } else if (this.isServerProvenEmptyRead(res)) {
          this.readError = ''
          const content = responseContent(res)
          const list = this.extractRewardHistory(content)
          this.mergeRewardList(list)
          this.finished = true
        } else if (hasUnavailableMarker(res)) {
          this.finished = true
          this.preserveReadError(res)
        } else if (isOkResponse(res)) {
          this.readError = ''
          const content = responseContent(res)
          const list = this.extractRewardHistory(content)
          this.mergeRewardList(list)
          this.finished = list.length < this.pageSize
          if (!this.finished) this.pageIndex += 1
        } else {
          this.finished = true
          this.preserveReadError(res)
        }
      } catch (e) {
        this.finished = true
        this.preserveReadError(e)
      } finally {
        this.loading = false
      }
    },
    extractRewardHistory(content) {
      if (!content || typeof content !== 'object') return []
      const directRows = content.rewardHistoryList ||
        content.signRecordList ||
        content.signHistoryList ||
        content.list ||
        content.records ||
        content.dataList ||
        content.pageList ||
        content.items ||
        null
      const rows = Array.isArray(directRows) ? directRows : this.findRewardHistoryRows(content)
      if (!Array.isArray(rows)) return []
      return rows
        .map(item => {
          const row = item || {}
          const type = this.firstRewardValue(row.type, row.activityType, row.rewardType, 'regular')
          const date = this.firstRewardValue(
            row.date,
            row.signedDay,
            row.signed_day,
            row.signDate,
            row.createDate,
            row.create_date,
            row.createdAt,
            row.time
          )
          return {
            type,
            rewardId: row.rewardId,
            id: row.id,
            amount: this.safeNumber(this.firstRewardValue(
              row.amount,
              row.rewardAmount,
              row.awardAmount,
              row.giftAmount,
              row.bonusAmount
            )),
            date,
            signDate: this.firstRewardValue(row.signDate, row.signedDay, row.signed_day, row.date),
            createDate: this.firstRewardValue(row.createDate, row.create_date, row.createdAt, row.time),
            status: row.status,
            isPremium: row.isPremium === true || row.isPremium === 1 || type === 'deposit'
          }
        })
        .filter(item => item.date)
        .sort((a, b) => new Date(b.date) - new Date(a.date))
    },
    findRewardHistoryRows(source, depth = 0, seen = new Set()) {
      if (!source || typeof source !== 'object' || seen.has(source) || depth > 6) return []
      seen.add(source)
      for (const key of REWARD_HISTORY_LIST_KEYS) {
        if (Array.isArray(source[key])) return source[key]
      }
      for (const key of REWARD_HISTORY_ENVELOPE_KEYS) {
        const value = source[key]
        if (Array.isArray(value)) return value
        if (value && typeof value === 'object') {
          const rows = this.findRewardHistoryRows(value, depth + 1, seen)
          if (Array.isArray(rows) && rows.length) return rows
        }
      }
      return []
    },
    firstRewardValue(...values) {
      return values.find(value => value !== undefined && value !== null && value !== '')
    },
    mergeRewardList(list) {
      if (!Array.isArray(list) || list.length === 0) return
      const merged = []
      const seen = new Set()
      this.rewardList.concat(list).forEach(item => {
        const key = this.rewardIdentity(item)
        if (key) {
          if (seen.has(key)) return
          seen.add(key)
          merged.push(item)
        } else {
          merged.push(item)
        }
      })
      this.rewardList = merged
    },
    rewardIdentity(item) {
      if (!item) return ''
      if (item.rewardId !== undefined && item.rewardId !== null && item.rewardId !== '') return `rewardId:${item.rewardId}`
      if (item.id !== undefined && item.id !== null && item.id !== '') return `id:${item.id}`

      const dateValue = item.date || item.signDate || item.createDate
      const hasAmount = item.amount !== undefined && item.amount !== null && item.amount !== ''
      const hasStatus = item.status !== undefined && item.status !== null && item.status !== ''
      const hasCompositeIdentity = Boolean(dateValue) && hasAmount && hasStatus
      const identityParts = []
      if (dateValue) identityParts.push(`date:${dateValue}`)
      if (hasAmount) identityParts.push(`amount:${item.amount}`)
      if (hasStatus) identityParts.push(`status:${item.status}`)
      return hasCompositeIdentity && identityParts.length ? identityParts.join('|') : ''
    },
    safeNumber(value, fallback = 0) {
      const number = Number(value)
      return Number.isFinite(number) ? number : fallback
    },
    loadMore() {
      if (this.finished || this.loading) return
      this.fetchRewards()
    },
    onDateConfirm(date) {
      this.filterDate = date
      this.showDatePicker = false
    },
    clearFilter() {
      this.filterDate = null
    },
    formatDate(dateStr) {
      const dt = new Date(dateStr)
      if (isNaN(dt.getTime())) return ''
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const h = dt.getHours()
      const m = String(dt.getMinutes()).padStart(2, '0')
      const ampm = h >= 12 ? 'PM' : 'AM'
      const h12 = h % 12 || 12
      return `${monthNames[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()} • ${String(h12).padStart(2, '0')}:${m} ${ampm}`
    },
    formatFilterDate(date) {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
    }
  }
}
</script>

<style lang="less" scoped>
@primary: @brand;     /* 柠檬绿→火焰橙(强调/激活) */
@bg-dark: @vk-bg;     /* 页面底 */
@surface: @vk-surface;/* 卡片面 */

.reward-page-wrap {
  min-height: 100vh;
  background: @bg-dark;
  padding-bottom: 40px;
}

// Header
.reward-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  position: sticky;
  top: 0;
  z-index: 20;
  background: @bg-dark;
  border-bottom: 1px solid fade(@primary, 10%);

  &__back {
    width: 48px;
    display: flex;
    align-items: center;
    justify-content: flex-start;

    button, div {
      padding: 8px;
      border-radius: 50%;
      cursor: pointer;
      &:active {
        background: fade(@primary, 10%);
      }
    }
  }

  &__title {
    color: #ffffff;
    font-size: var(--vk-fs-md);
    font-weight: bold;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    flex: 1;
    text-align: center;
  }

  &__filter {
    width: 48px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    cursor: pointer;
    padding: 8px;
    border-radius: 50%;
    &:active {
      background: fade(@primary, 10%);
    }
  }
}

// Filter Tag
.reward-filter-tag {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 12px 16px 0;
  padding: 6px 12px;
  background: fade(@primary, 10%);
  border: 1px solid fade(@primary, 20%);
  border-radius: 20px;
  width: fit-content;

  span {
    color: @primary;
    font-size: var(--vk-fs-xs);
    font-weight: 600;
  }
}

// Content
.reward-content {
  padding: 8px 16px;
}

.reward-load-more {
  width: 100%;
  height: 42px;
  margin-top: 16px;
  border: 1px solid fade(@primary, 32%);
  border-radius: 8px;
  background: transparent;
  color: @primary;
  font-size: var(--vk-fs-sm);
  font-weight: 700;

  &:disabled {
    opacity: 0.55;
  }
}

.reward-read-error {
  margin: 8px 0 12px;
  padding: 10px 12px;
  border: 1px solid fade(#f5455c, 35%);
  border-radius: 8px;
  background: fade(#f5455c, 10%);
  color: #fecaca;
  font-size: var(--vk-fs-xs);
  line-height: 1.5;
}

// Group
.reward-group {
  margin-bottom: 16px;

  &__header {
    position: sticky;
    top: 56px;
    background: @bg-dark;
    padding: 12px 0;
    z-index: 10;

    h3 {
      color: var(--vk-t2);
      font-size: var(--vk-fs-sm);
      font-weight: bold;
      letter-spacing: 0.05em;
      text-transform: uppercase;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}

// Reward Item
.reward-item {
  display: flex;
  align-items: center;
  gap: 16px;
  background: @surface;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid transparent;
  transition: border-color 0.2s;

  &:active {
    border-color: fade(@primary, 30%);
  }

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: fade(#eab308, 10%);
    border: 1px solid fade(#eab308, 20%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__title-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__type {
    color: #ffffff;
    font-size: var(--vk-fs-base);
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__premium {
    background: fade(@primary, 10%);
    color: @primary;
    font-size: var(--vk-fs-2xs);
    font-weight: 900;
    padding: 2px 6px;
    border-radius: 4px;
    border: 1px solid fade(@primary, 20%);
    letter-spacing: -0.02em;
    white-space: nowrap;
  }

  &__date {
    color: var(--vk-t2);
    font-size: var(--vk-fs-xs);
    font-weight: 500;
    margin-top: 4px;
  }

  &__amount {
    flex-shrink: 0;

    p {
      color: @primary;
      font-size: var(--vk-fs-lg);
      font-weight: bold;
    }
  }
}

// Empty
.reward-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px 0;

  p {
    color: var(--vk-t3);
    font-size: var(--vk-fs-base);
  }
}

// PC
@media (min-width: 769px) {
  .reward-page-wrap {
    max-width: var(--vk-pc-col);
    margin: 0 auto;
  }
}
</style>
